import React, { useMemo } from 'react';
import classNames from 'classnames';
import { MultiSelect } from '../../../Controls/Select/MultiSelect';
import { useFilterField } from '../../hooks/useFilterField';
import { useFilterBarContext } from '../../state/FilterBarContext';
import type { SelectMultiFilterConfig } from '../../types';

import cn from './styles.module.scss';

export interface MultiSelectFilterControlProps {
  name: string;
  /** Overrides config.placeholder. */
  placeholder?: string;
  /** Render a static label inside the control's prefix slot. Forwarded to
   *  antd's native `prefix` prop on MultiSelect (inherited from BaseSelect). */
  prefix?: React.ReactNode;
}

/**
 * Renders a SELECT_MULTI field's control. Sibling of `SingleSelectFilterControl`.
 *
 * Differences vs the library's default `MultiSelect` chrome:
 *   - `maxDisplayChars` opts into the char-budget tag display: MultiSelect
 *     computes `maxTagCount` from the value it is DISPLAYING, so the budget
 *     stays in sync with the rendered tags even while a debounced `onChange`
 *     has not yet delivered the new value to the FilterBar store.
 *   - `maxTagPlaceholder` is forced to `undefined` so antd uses its default
 *     overflow chip ("+N"). The library's default — a label-as-placeholder
 *     pseudo-tag while empty — only makes sense when no other label/prefix is
 *     present, but FilterBar always supplies a `prefix` (the field label).
 *   - `suffixIcon` is forced to `undefined` so antd uses its default chevron.
 *     The library's default — a counter Badge + caret — duplicates the "+N"
 *     overflow chip and adds chrome the FilterBar context doesn't need.
 *   - `classNames.item` adds a tag-background class. The global Select theme
 *     sets `multipleItemBg: 'none'` because the rest of the app uses
 *     `MultiSelect` as a label-as-placeholder picker that needs a transparent
 *     tag background; FilterBar's use is the opposite — chips need a visible
 *     fill — so the background is re-introduced locally via this className.
 */

const MAX_DISPLAY_CHARS = 24;

export const MultiSelectFilterControl: React.FC<MultiSelectFilterControlProps> = ({
  name,
  placeholder: placeholderOverride,
  prefix,
}) => {
  const { value, options, setValue, config, isLoading } = useFilterField(name);
  const { size } = useFilterBarContext();

  const selectedValues = useMemo(
    () => (value as Array<string | number> | undefined) ?? [],
    [value],
  );

  if (!config || config.type !== 'SELECT_MULTI') return null;

  const cfg = config as SelectMultiFilterConfig;
  const size_ = cfg.size ?? size;
  const placeholder = placeholderOverride ?? cfg.placeholder;
  const { classNames: userClassNames, ...restControlProps } = (cfg.controlProps ?? {}) as
    Record<string, unknown> & { classNames?: { item?: string } };
  const userClassNamesObj = (typeof userClassNames === 'object' && userClassNames !== null)
    ? userClassNames
    : {};

  return (
    <MultiSelect
      {...restControlProps}
      multiple
      options={options}
      value={selectedValues}
      onChange={(v) => setValue(v)}
      size={size_}
      placeholder={placeholder}
      prefix={prefix}
      showSelectAllOption={cfg.showSelectAll}
      loading={isLoading}
      disabled={isLoading || (restControlProps as { disabled?: boolean }).disabled}
      debounceTime={cfg.debounceMs}
      showSearch={cfg.searchable !== false}
      allowClear={cfg.clearable !== false}
      maxDisplayChars={MAX_DISPLAY_CHARS}
      maxTagPlaceholder={undefined}
      suffixIcon={undefined}
      classNames={{
        ...userClassNamesObj,
        item: classNames(cn.tagItem, userClassNamesObj.item),
      }}
    />
  );
};

